/**
 * Navbar Component
 * Main navigation bar with responsive mobile menu
 * Displays navigation links, authentication controls, and user info
 */

import { Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { SignInButton } from '../auth/SignInButton';
import { SignOutButton } from '../auth/SignOutButton';
import { UserInfo } from '../auth/UserInfo';
import { CreditDisplay } from '../auth/CreditDisplay';
import { ThemeToggle } from './ThemeToggle';

/**
 * Navbar component
 * Provides site navigation with responsive hamburger menu for mobile
 * Shows authentication status and user credits when logged in
 * 
 * @returns Navigation bar component
 */
export function Navbar() {
  const { isAuthenticated, user } = useAuthContext();
  const { profile, isLoading: profileLoading } = useUserProfile();

  // Check if user is admin (you can adjust this logic based on your auth setup)
  const isAdmin = user?.email?.includes('admin') || false;

  return (
    <nav 
      className="navbar-container"
      style={{
        width: '100%',
        background: 'var(--color-surface)',
        color: 'var(--color-fg)',
        fontFamily: 'var(--font-family)',
        fontSize: 'var(--font-size-xs)',
        borderBottom: '4px solid var(--color-accent)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        marginBottom: '1.25rem'
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
        <div className="navbar-content">
          {/* Logo */}
          <Link 
            to="/" 
            className="navbar-logo"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.25rem 0',
              textDecoration: 'none'
            }}
          >
            <img 
              src="https://s3.us-west-2.amazonaws.com/www.8bitcomposer.com/logo.png" 
              alt="8-Bit Composer Logo" 
              style={{ height: '2rem', width: '2rem' }}
            />
            <span style={{ color: 'var(--color-accent)', fontSize: 'var(--font-size-sm)' }}>
              8-Bit Composer
            </span>
          </Link>
          
          {/* Navigation links */}
          <div className="navbar-links">

          {isAdmin && (
            <Link
              to="/admin"
              style={{
                border: '2px solid var(--color-accent)',
                padding: '0.5rem 1rem',
                fontSize: 'var(--font-size-xs)',
                borderRadius: '4px',
                textDecoration: 'none',
                color: 'var(--color-fg)',
                transition: 'all 0.2s'
              }}
            >
              Admin 📊
            </Link>
          )}
          <Link 
            to="/"
            style={{
              color: 'var(--color-fg)',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
          >
            Home
          </Link>
          {isAuthenticated && (
            <>
              <Link 
                to="/profile"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
              >
                Profile
              </Link>
              <Link 
                to="/my-compositions"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
              >
                My Compositions
              </Link>
            </>
          )}
          <Link 
            to="/about"
            style={{
              color: 'var(--color-fg)',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
          >
            About
          </Link>
          <Link 
            to="/contact"
            style={{
              color: 'var(--color-fg)',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
          >
            Contact
          </Link>
          <ThemeToggle />
          <SignInButton />
          <SignOutButton />
          </div>
        </div>
      </div>
      
      {/* User info and credits section */}
      {isAuthenticated && (
        <div style={{
          textAlign: 'center',
          color: 'var(--color-accent)',
          fontSize: 'var(--font-size-xs)',
          marginTop: '0.25rem',
          paddingBottom: '0.5rem'
        }}>
          {/* Loading skeleton */}
          {profileLoading && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '0.5rem' 
            }}>
              <div style={{
                width: '150px',
                height: '16px',
                background: 'var(--color-bg-secondary)',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
              <div style={{
                width: '100px',
                height: '16px',
                background: 'var(--color-bg-secondary)',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
            </div>
          )}
          
          {/* Profile loaded - show user info and credits */}
          {!profileLoading && profile && (
            <>
              {/* User profile section with picture and name */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '0.5rem',
                marginBottom: '0.5rem'
              }}>
                {/* Profile picture */}
                {profile.profilePictureUrl && (
                  <img 
                    src={profile.profilePictureUrl} 
                    alt={`${profile.name}'s profile`}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      border: '2px solid var(--color-accent)'
                    }}
                  />
                )}
                {/* User name */}
                <span style={{ color: 'var(--color-accent)' }}>
                  🎵 Welcome, {profile.name || profile.email}
                </span>
              </div>
              
              {/* Credits display - using CreditDisplay component which handles its own state */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '0.75rem' 
              }}>
                <CreditDisplay />
              </div>
            </>
          )}
          
          {/* Fallback to old components if profile not available */}
          {!profileLoading && !profile && (
            <>
              <div style={{ marginBottom: '0.5rem' }}>
                <UserInfo />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
                <CreditDisplay />
              </div>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
